import { useRef, useCallback } from 'react';
import { AudioCapture } from '../lib/audioCapture';
import { frequencyToNote } from '../lib/noteMap';
import { useVeloraStore } from '../store/useVeloraStore';
import { VeloraWS } from '../lib/websocket';
import { v4 as uuid } from 'uuid';

const STABILITY_WINDOW = 20; // frames (~200ms at 10fps)
const TARGET_FPS = 10;

export function useAudio() {
  const capture = useRef<AudioCapture | null>(null);
  const wsRef = useRef<VeloraWS | null>(null);
  const frameTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const recentFreqs = useRef<number[]>([]);
  const lastFrame = useRef<{ frequency: number; rms: number } | null>(null);

  const store = useVeloraStore();

  const start = useCallback(async () => {
    store.startRecording();

    // Connect WebSocket if enabled
    if (store.wsEnabled) {
      const ws = new VeloraWS(
        store.desktopHost,
        (msg) => store.addFeedback({ ...msg, id: uuid() }),
        (connected) => store.setConnected(connected)
      );
      wsRef.current = ws;
      ws.connect();
    }

    // Start audio capture
    capture.current = new AudioCapture((frame) => {
      lastFrame.current = frame;
      if (frame.frequency > 0) {
        recentFreqs.current = [...recentFreqs.current, frame.frequency].slice(-STABILITY_WINDOW);
      }
    });

    await capture.current.start();

    // Dispatch pitch frames at TARGET_FPS
    frameTimer.current = setInterval(() => {
      const frame = lastFrame.current;
      if (!frame || frame.frequency <= 0) {
        store.updatePitch(0, '--', 0, 0);
        return;
      }

      const info = frequencyToNote(frame.frequency);

      // Stability = 1 - (normalized std deviation of recent freqs)
      let stability = 1;
      if (recentFreqs.current.length >= 5) {
        const mean = recentFreqs.current.reduce((a, b) => a + b, 0) / recentFreqs.current.length;
        const variance = recentFreqs.current.reduce((sum, f) => sum + (f - mean) ** 2, 0) / recentFreqs.current.length;
        const stdDev = Math.sqrt(variance);
        stability = Math.max(0, 1 - (stdDev / mean) * 10);
      }

      store.updatePitch(frame.frequency, info.fullName, info.cents, stability);

      wsRef.current?.sendFrame({
        timestamp: Date.now(),
        frequency: frame.frequency,
        note: info.fullName,
        cents_off: info.cents,
        stability,
      });
    }, 1000 / TARGET_FPS);
  }, [store]);

  const stop = useCallback(() => {
    if (frameTimer.current) {
      clearInterval(frameTimer.current);
      frameTimer.current = null;
    }
    capture.current?.stop();
    capture.current = null;
    wsRef.current?.disconnect();
    wsRef.current = null;
    store.stopRecording();
  }, [store]);

  return { start, stop };
}
